import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtUSD, fmtDate, SITUATIONS } from "@/lib/api";
import { Plus, Home as HomeIcon, FileText, MessageSquare, Calendar } from "lucide-react";

export default function SellerDashboard() {
  const navigate = useNavigate();
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties/mine");
        setProps(data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow">Seller dashboard</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Your properties</h1>
          <p className="text-[var(--adn-text-soft)] mt-2 text-sm">Submit a property to start receiving solution paths and offers.</p>
        </div>
        <Link to="/submit-property" data-testid="sd-submit" className="btn-gold px-5 py-3 rounded text-sm inline-flex items-center gap-2 self-start">
          <Plus size={16} /> Submit Property
        </Link>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-[var(--adn-text-soft)]">Loading…</div>
      ) : props.length === 0 ? (
        <div className="mt-10 bg-white border border-[var(--adn-line)] p-10 text-center">
          <HomeIcon className="mx-auto text-[var(--adn-text-muted)]" size={28} />
          <p className="font-display text-xl font-semibold mt-3">No properties yet</p>
          <p className="text-[var(--adn-text-soft)] text-sm mt-2">Tell us about your property to see your options.</p>
          <Link to="/submit-property" className="btn-gold inline-flex mt-6 px-5 py-3 rounded text-sm">Submit Property</Link>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {props.map((p) => (
            <Link
              to={`/properties/${p.property_id}`}
              key={p.property_id}
              data-testid={`prop-${p.property_id}`}
              className="bg-white border border-[var(--adn-line)] p-6 card-hover block"
            >
              <div className="flex items-center justify-between">
                <span className="pill pill-neutral">{p.status}</span>
                <span className="text-xs text-[var(--adn-text-muted)] font-mono">{fmtDate(p.created_at)}</span>
              </div>
              <h3 className="font-display text-lg font-semibold mt-3 leading-tight">{p.address_line1}</h3>
              <p className="text-[var(--adn-text-soft)] text-sm">{p.city}, {p.state} {p.zip_code}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(p.situations || []).slice(0, 3).map((s) => {
                  const meta = SITUATIONS.find(x => x.id === s);
                  return <span key={s} className={`pill ${meta?.pill || "pill-neutral"}`}>{meta?.label || s}</span>;
                })}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
                <Stat label="Value" v={fmtUSD(p.estimated_value)} />
                <Stat label="Mortgage" v={fmtUSD(p.mortgage_balance)} />
                <Stat label="Score" v={p.ai?.distress_score ?? "—"} accent />
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--adn-text-muted)]">{label}</div>
      <div className={`font-display text-base tabular ${accent ? "text-[var(--adn-gold)]" : ""}`}>{v}</div>
    </div>
  );
}
